import { useState } from "react";
import { useDispatch } from "react-redux";
import { Save, ShieldAlert, Bot, UserPlus } from "lucide-react";
import { showAlert } from "../../../../store/alertSlice";
import { Textarea } from "../../../../components/tailwind/textarea";
import { Button } from "@aws-amplify/ui-react";
import { Label } from "../../../../components/tailwind/label";
import { Input } from "../../../../components/tailwind/input";
import { Switch } from "../../../../components/ui/switch";
import { workflowTheme } from "../../utils/workflowBuilderTheme";
import { waBtn } from "../../utils/whatsappUiTokens";
import { cn } from "../../../../lib/utils";

const guardrailRowClass = `flex items-center justify-between p-3 border ${workflowTheme.border} rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors`;

export function AIOrchestratorTab() {
  const dispatch = useDispatch();
  const [persona, setPersona] = useState("You are a helpful customer support assistant for our electronics brand. You must only answer questions based on the uploaded Knowledge Base documents. If you don't know the answer, politely offer to escalate to a human agent.");
  const [guardrails, setGuardrails] = useState({
    businessTopics: true,
    escalateLowConfidence: true,
    allowRefunds: false,
    internalNotes: true
  });
  const [triggerPhrases, setTriggerPhrases] = useState("talk to human, agent, representative, help, support, escalate");
  const [autoEscalateTimeout, setAutoEscalateTimeout] = useState("24h");

  const handleSave = () => {
    dispatch(showAlert({
      message: "Persona, guardrails, and handoff settings have been updated for this workflow.",
      type: "success"
    }));
  };

  return (
    <div className={`flex-1 flex flex-col h-full ${workflowTheme.page} overflow-hidden`}>
      <div className="max-w-4xl mx-auto w-full p-8 overflow-y-auto flex-1">
        <div className="mb-8">
          <h2 className={`text-2xl font-bold ${workflowTheme.textPrimary} mb-2`}>AI Orchestrator</h2>
          <p className={workflowTheme.textSecondary}>Configure how the AI Response node behaves during a live workflow run.</p>
        </div>

        <div className={`${workflowTheme.surface} rounded-xl border ${workflowTheme.border} shadow-sm p-6 mb-6`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#EAF3DE] dark:bg-emerald-950/50 rounded-lg flex items-center justify-center">
              <Bot className="w-5 h-5 text-[#27500A] dark:text-emerald-400" />
            </div>
            <div>
              <h3 className={`font-semibold ${workflowTheme.textPrimary}`}>AI Persona & System Prompt</h3>
              <p className={`text-xs ${workflowTheme.textSecondary}`}>Instruct the AI on its role, tone, and specific rules of engagement.</p>
            </div>
          </div>
          
          <div className="space-y-3">
            <Label className={`text-xs font-bold ${workflowTheme.textSecondary} uppercase`}>System Prompt Context</Label>
            <Textarea 
              value={persona}
              onChange={e => setPersona(e.target.value)}
              className="min-h-[150px] text-sm resize-y"
              placeholder="E.g., You are a sales rep for..."
            />
            <p className={`text-xs ${workflowTheme.textMuted} mt-1`}>
              Note: The AI will automatically have access to all active files in the Knowledge Base tab.
            </p>
          </div>
        </div>

        <div className={`${workflowTheme.surface} rounded-xl border ${workflowTheme.border} shadow-sm p-6 mb-6`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-orange-50 dark:bg-orange-950/50 rounded-lg flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h3 className={`font-semibold ${workflowTheme.textPrimary}`}>Guardrails</h3>
              <p className={`text-xs ${workflowTheme.textSecondary}`}>Pre-built safety rules to protect your brand during automated conversations.</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className={guardrailRowClass}>
              <div>
                <h4 className={`text-sm font-semibold ${workflowTheme.textPrimary}`}>Restrict to business topics only</h4>
                <p className={`text-xs ${workflowTheme.textSecondary}`}>AI will refuse to discuss anything unrelated to your products/services.</p>
              </div>
              <Switch 
                checked={guardrails.businessTopics} 
                onCheckedChange={c => setGuardrails(prev => ({...prev, businessTopics: c}))}
                className="data-[state=checked]:bg-[hsl(158, 48%, 41%)]"
              />
            </div>
            
            <div className={guardrailRowClass}>
              <div>
                <h4 className={`text-sm font-semibold ${workflowTheme.textPrimary}`}>Escalate if confidence below 70%</h4>
                <p className={`text-xs ${workflowTheme.textSecondary}`}>If the AI isn't sure, it won't guess. It will ask a human to step in.</p>
              </div>
              <Switch 
                checked={guardrails.escalateLowConfidence} 
                onCheckedChange={c => setGuardrails(prev => ({...prev, escalateLowConfidence: c}))}
                className="data-[state=checked]:bg-[hsl(158, 48%, 41%)]"
              />
            </div>
            
            <div className={guardrailRowClass}>
              <div>
                <h4 className={`text-sm font-semibold ${workflowTheme.textPrimary}`}>Allow refund/replacement discussion</h4>
                <p className={`text-xs ${workflowTheme.textSecondary}`}>Permit the AI to handle standard refund policies defined in the Knowledge Base.</p>
              </div>
              <Switch 
                checked={guardrails.allowRefunds} 
                onCheckedChange={c => setGuardrails(prev => ({...prev, allowRefunds: c}))}
                className="data-[state=checked]:bg-[hsl(158, 48%, 41%)]"
              />
            </div>

            <div className={guardrailRowClass}>
              <div>
                <h4 className={`text-sm font-semibold ${workflowTheme.textPrimary}`}>Never share internal notes</h4>
                <p className={`text-xs ${workflowTheme.textSecondary}`}>The AI will never leak context instructions or internal CRM data.</p>
              </div>
              <Switch 
                checked={guardrails.internalNotes} 
                onCheckedChange={c => setGuardrails(prev => ({...prev, internalNotes: c}))}
                className="data-[state=checked]:bg-[hsl(158, 48%, 41%)]"
              />
            </div>
          </div>
        </div>

        <div className={`${workflowTheme.surface} rounded-xl border ${workflowTheme.border} shadow-sm p-6 mb-8`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-50 dark:bg-blue-950/50 rounded-lg flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className={`font-semibold ${workflowTheme.textPrimary}`}>Human Handoff</h3>
              <p className={`text-xs ${workflowTheme.textSecondary}`}>When and how the AI passes the conversation to a human agent.</p>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="space-y-2">
              <Label className={`text-sm font-semibold ${workflowTheme.textPrimary}`}>Trigger Phrases</Label>
              <p className={`text-xs ${workflowTheme.textSecondary} mb-2`}>If the customer says any of these, the AI will immediately stop replying and notify your team.</p>
              <Input 
                value={triggerPhrases}
                onChange={e => setTriggerPhrases(e.target.value)}
                placeholder="Comma-separated phrases..."
                className="text-sm"
              />
            </div>
            
            <div className="space-y-2">
              <Label className={`text-sm font-semibold ${workflowTheme.textPrimary}`}>Auto-escalate after timeout</Label>
              <p className={`text-xs ${workflowTheme.textSecondary} mb-2`}>If the conversation goes nowhere, assign it to a human.</p>
              <select 
                className={`w-full h-10 rounded-md border ${workflowTheme.border} ${workflowTheme.input} px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring`}
                value={autoEscalateTimeout}
                onChange={e => setAutoEscalateTimeout(e.target.value)}
              >
                <option value="12h">12 hours of inactivity</option>
                <option value="24h">24 hours of inactivity</option>
                <option value="48h">48 hours of inactivity</option>
                <option value="never">Never auto-escalate</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} className={cn("min-w-[120px]", waBtn.primary)}>
            <Save className="w-4 h-4 mr-2" /> Save Configuration
          </Button>
        </div>
      </div>
    </div>
  );
}
